import { createContext, FC, useCallback, useContext, useState } from 'react';

const STORAGE_KEY_TOKEN = 'guest_access_token';
const STORAGE_KEY_USER = 'guest_user';

export interface GuestCustomer {
    id: string;
    email: string;
    fullName: string;
    account: {
        id: string;
        slug: string;
        emailDomain: string;
    };
}

interface GuestAuthState {
    token: string | null;
    customer: GuestCustomer | null;
    loginGuest: (token: string, customer: GuestCustomer) => void;
    logoutGuest: () => void;
}

const GuestAuthContext = createContext<GuestAuthState>({
    token: null,
    customer: null,
    loginGuest: () => {},
    logoutGuest: () => {},
});

function readStorage(): { token: string | null; customer: GuestCustomer | null } {
    try {
        const token = localStorage.getItem(STORAGE_KEY_TOKEN);
        const raw = localStorage.getItem(STORAGE_KEY_USER);
        const customer = raw ? (JSON.parse(raw) as GuestCustomer) : null;
        return { token, customer };
    } catch {
        return { token: null, customer: null };
    }
}

export const GuestAuthProvider: FC = ({ children }) => {
    const [state, setState] = useState(readStorage);

    const loginGuest = useCallback((token: string, customer: GuestCustomer) => {
        localStorage.setItem(STORAGE_KEY_TOKEN, token);
        localStorage.setItem(STORAGE_KEY_USER, JSON.stringify(customer));
        setState({ token, customer });
    }, []);

    const logoutGuest = useCallback(() => {
        localStorage.removeItem(STORAGE_KEY_TOKEN);
        localStorage.removeItem(STORAGE_KEY_USER);
        setState({ token: null, customer: null });
    }, []);

    return (
        <GuestAuthContext.Provider value={{ ...state, loginGuest, logoutGuest }}>
            {children}
        </GuestAuthContext.Provider>
    );
};

export const useGuestAuth = () => useContext(GuestAuthContext);
